/**
 * EmptyFileError — the browser handed over a file with no bytes (BUG-676).
 *
 * Uploading it would store an empty object: the presigned S3 PUT accepts an
 * empty body with 200, and the send then fails downstream. Meta rejects the
 * WhatsApp send with 131053 "No media found from weblink and received http
 * code 200", and WhatsApp Web delivers a PDF that does not open. Thrown before
 * the upload, so nothing is stored or sent.
 *
 * The default message is an English line for logs. The chat passes its
 * translated copy, because its toast shows `error.message` as is.
 */
export class EmptyFileError extends Error {
  /** Stable discriminator for consumers that cannot use `instanceof`. */
  readonly code = 'empty_file' as const;

  readonly fileName: string;

  constructor(
    fileName: string,
    message = `File is empty (0 bytes): "${fileName}"`,
  ) {
    super(message);
    this.name = 'EmptyFileError';
    this.fileName = fileName;
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, EmptyFileError.prototype);
  }
}

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isEmptyFileError = (err: unknown): err is EmptyFileError =>
  err instanceof EmptyFileError ||
  (err instanceof Error &&
    'code' in err &&
    (err as { code?: unknown }).code === 'empty_file');
